import React from "react";
import Button from "../components/Button";
import Divider from "../components/Divider";
import QRScanner from "../components/QRScanner";
import { useAppContext } from "../context/appContext";
import { Calendar1, CircleCheckBig, CircleX, Ticket, User } from 'lucide-react';
import PageHeader from "../components/Header";
import { eventInfoApi, isUserLoggedInApi, scanTicketApi, signOutApi } from "../apis/authService";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { extractDataFromArray, logoutCookies, timeFormatter } from "../utils/utils&validatation";
import Tooltip from "../components/Tooltip";
import useCameraStatus from "./hooks/useCameraStatus";
// import useLandscapeScroll from "./hooks/useLandscapeScoll";

const TicketScanner = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
        isCameraActive,
        setIsCameraActive,
        totalCheckedIn,
        setTotalCheckedIn,
    } = useAppContext();

    const [eventInfo, setEventInfo] = React.useState({ event: "", time: "" });
    const [apiRes, setApiRes] = React.useState(null);
    const [qrCodeUrl, setQrCodeUrl] = React.useState(null);
    const [customerName, setCustomerName] = React.useState("");
    const [disabled, setDisabled] = React.useState(null);
    const [loading, setLoading] = React.useState(false);

    const [isLoggedIn, setIsLoggedIn] = React.useState("checking");

    const cameraStatus = useCameraStatus();

    const handleLogout = React.useCallback(async () => {
        try {
            const res = await signOutApi({ cmd: "signOut" });
            if (extractDataFromArray(res, ["errorCode"], 1) == 0) {
                logoutCookies();
                navigate("/event-scanner/login");
            }
        } catch (err) {
            console.error("Error inside signOutApi =>", err)
        }
    }, [navigate]);

    const handleReset = React.useCallback(() => {
        setQrCodeUrl(null);
        setApiRes(null);
        setIsCameraActive(true);
        setLoading(false);
        setCustomerName("");
    }, [setIsCameraActive]);

    React.useEffect(() => {
        const fetchUserStatus = async () => {
            try {
                const res = await isUserLoggedInApi({ cmd: "isUserLoggedIn" });
                if (extractDataFromArray(res, ["errorCode"], "") !== 1) {
                    window.user_info = extractDataFromArray(res, ["data"], {});
                    setIsLoggedIn("logged-in");
                } else {
                    setIsLoggedIn("logged-out");
                }
            } catch (err) {
                console.log("Error checking login:", err);
                setIsLoggedIn("logged-out");
            }
        };
        fetchUserStatus();
    }, []);

    React.useEffect(() => {
        if (isLoggedIn === "logged-out") handleLogout();
    }, [isLoggedIn, handleLogout]);

    // Scan valid ticket based on URL extracted from the QR
    const handleScanTicket = React.useCallback(
        async (code) => {
            setLoading(true);
            try {
                const response = await scanTicketApi({
                    cmd: "scanAndGetTicketStatus",
                    full_path: code,
                    event_id: id,
                });

                const status = response?.data?.status;
                const name = response?.data?.customer_name || "";

                if (status === "D") {
                    setCustomerName(name);
                    setApiRes("duplicate");
                } else if (status === "V") {
                    setCustomerName(name);
                    setApiRes("pass");
                } else setApiRes("invalid");
            } catch (err) {
                console.log("Scan error:", err);
                setApiRes("invalid");
            }
        },
        [id,]
    );

    React.useEffect(() => {
        if (qrCodeUrl) handleScanTicket(qrCodeUrl);
    }, [qrCodeUrl, handleScanTicket]);

    React.useEffect(() => {
        if (isLoggedIn !== "logged-in") return;

        const fetchEvent = async () => {
            try {
                const response = await eventInfoApi({
                    cmd: "fetchIndividualEvent",
                    event_id: id,
                });

                if (response?.errorCode === 1) {
                    setDisabled("error");
                    setTotalCheckedIn("0/0");
                    return;
                }

                const { event, total_allowed, total_checked_in } = response.data;
                setEventInfo({ event: event === "" ? "NA" : event, time: timeFormatter(response?.data) });
                setDisabled("pass");
                setTotalCheckedIn(`${total_checked_in}/${total_allowed}`);
            } catch (err) {
                console.log("Event info error:", err);
            }
        };

        fetchEvent();
    }, [isLoggedIn, id, apiRes, setTotalCheckedIn]);

    React.useEffect(() => {
        const setFullHeight = () => {
            document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
        };
        setFullHeight();
        window.addEventListener("resize", setFullHeight);
        return () => window.removeEventListener("resize", setFullHeight);
    }, []);

    React.useEffect(() => {
        if (apiRes !== null) setIsCameraActive(false);
        else setIsCameraActive(true);
    }, [apiRes, isCameraActive]);

    // const scrollAllowed = useLandscapeScroll();

    if (disabled === null) {
        return <Loader />;
    }

    if (disabled === "error") {
        return (
            <div className="w-full"
                style={{
                    border: "1px solid #fafafc",
                    backgroundColor: "#f9fafb",
                    height: "100vh",

                }}>
                <PageHeader />
                <Divider />
                <div className="w-full flex col-flex justify-center items-center g-8" style={{ minHeight: "calc(100% - 73px)" }}>
                    <img src="https://qrcodechimp.s3.amazonaws.com/sysconf/no_event.svg" style={{ minWidth: "35%", width: "65%", margin: "0 auto" }} alt="no_event.svg" />
                    <p style={{ fontSize: "20px" }}>No event ticket found</p>
                    <Button buttonText="Go back"
                        onClick={() => { navigate("/event-scanner/event-slots"); }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className="w-full"
            style={{
                border: "1px solid #fafafc",
                backgroundColor: "#f9fafb",
                height: "100vh",

            }}
        >
            <PageHeader />
            <Divider />

            {/* event info */}
            {disabled === "pass" ?
                <div
                    className="flex col-flex justify-center br-8 bc-white w-auto h-auto g-3"
                    style={{
                        padding: "8px 16px",
                        border: "1px solid #e2e7f1",
                        margin: "12px 16px",
                    }}
                >
                    <div className="flex items-center g-2 p-1">
                        <Ticket style={{ width: 20, height: "auto", flexShrink: 0, }} className="mr-2" />
                        <Tooltip content={eventInfo.event}>
                            <p
                                className="font-sm flex items-center"
                                style={{
                                    height: "max-content",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                    cursor: "default",
                                }}
                            >
                                Event: <b>{eventInfo.event}</b>
                            </p>
                        </Tooltip>
                    </div>

                    {eventInfo?.time !== "NA" && (
                        <div className="flex items-center g-2 p-1">
                            <Calendar1 style={{ width: 20, height: "auto", flexShrink: 0, }} className="mr-2" />
                            <Tooltip content={eventInfo.time}>
                                <p
                                    className="font-sm flex items-center"
                                    style={{
                                        height: "max-content",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        cursor: "default",
                                    }}
                                >
                                    Slot: <b>{eventInfo?.time}</b>
                                </p>
                            </Tooltip>
                        </div>
                    )}

                    <div className="flex items-center g-2 p-1">
                        <User style={{ width: 20, height: "auto", flexShrink: 0, }} className="mr-2" />
                        <p
                            className="font-sm flex items-center"
                            style={{
                                height: "max-content",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                            }}
                        >
                            Total checked-in: <b>{totalCheckedIn}</b>
                        </p>
                    </div>
                </div>
                :
                null
            }

            {/* scanner */}
            {(eventInfo) && (
                <div className="w-full flex relative" style={{ maxHeight: "calc(var(--vh, 1vh) * 60)", minHeight: "calc(var(--vh, 1vh) * 55)", height: "fit-content" }}>
                    {(loading && apiRes === null) ? (
                        <div className="flex justify-center items-center w-full" style={{ backgroundColor: "#f3f4f6", border: "1px solid #e2e7f1" }}>
                            <Loader />
                        </div>
                    ) : (
                        <QRScanner qrCodeUrl={qrCodeUrl} setQrCodeUrl={setQrCodeUrl} />
                    )}
                    {apiRes && loading ? (
                        <div
                            className="flex col-flex justify-center z-10 br-8 absolute transform-center top left bc-white"
                            style={{
                                height: "216px",
                                width: "216px",
                                border: "1px solid #fafafc",
                                padding: 20,
                                gap: "12px",
                            }}
                        >
                            <div
                                className="flex col-flex g-4 items-center inherit-bgcolor"
                            >
                                {apiRes === "pass" ?
                                    <CircleCheckBig color={"#00a63e"} />
                                    : apiRes === "duplicate" ?
                                        <CircleX color={"#d08700"} />
                                        :
                                        <CircleX color={"#e7000b"} />
                                }
                                <h3 className="m-0 bc-white" style={{ color: apiRes === "pass" ? "#00a63e" : apiRes === "duplicate" ? "#d08700" : "#e7000b", }}>
                                    {apiRes === "pass" ? "Valid Ticket" : apiRes === "duplicate" ? "Duplicate Entry" : "Invalid Ticket"}
                                </h3>
                            </div>
                            {
                                customerName && <Tooltip content={customerName}>
                                    <p className="font-sm header-p"
                                        style={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            cursor: "default",
                                        }}><b>Customer:</b> {customerName}</p>
                                </Tooltip>
                            }
                            {(apiRes === "pass" || apiRes === "duplicate") ?
                                <>
                                    <Tooltip content={eventInfo.event}>
                                        <p className="font-sm header-p"
                                            style={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                wordBreak: "break-word",
                                                whiteSpace: "normal",
                                                cursor: "default",
                                            }}><b>Event:</b> {eventInfo?.event}</p>
                                    </Tooltip>
                                    {
                                        eventInfo?.time !== "NA" && <Tooltip content={eventInfo.time}>
                                            <p className="font-sm header-p"
                                                style={{
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    cursor: "default",
                                                }}><b>Slot:</b> {eventInfo.time}</p>
                                        </Tooltip>
                                    }
                                </>
                                :
                                <p style={{ fontWeight: 300 }} className="font-sm header-p text-center">This ticket could not be verified. Please scan a valid ticket.</p>
                            }
                        </div>
                    ) : (
                        <div
                            className="flex col-flex justify-center items-center z-20 br-8 absolute transform-center top left screen-overlay"
                            style={{
                                minWidth: "150px",
                                maxWidth: "216px",
                                minHeight: "150px",
                                maxHeight: "216px",
                                backgroundColor: "transparent",
                                padding: 30,
                            }}
                        >
                            {/* {
                                !scrollAllowed &&
                                <Divider style={{
                                    width: "100%",
                                    border: !loading ? "1px solid #05df72" : "1px solid #f3f4f6",
                                    position: "absolute",
                                    top: "0%",
                                    left: "0%",
                                    transform: "translate(0%, -50%)",
                                }} />
                            } */}{
                                !cameraStatus &&
                                <p style={{ color: "#e7000b", textAlign: "center", fontSize: "28px", }}>Camera access is denied</p>
                            }
                            <div className="absolute transform-center top left screen-overlay" style={{
                                width: "80%",
                                minWidth: "120px",
                                maxWidth: "185px",
                                height: "80%",
                                minHeight: "120px",
                                maxHeight: "185px",
                                border: !loading && "4px solid #05df72",
                                borderRadius: "0.375rem",
                                backgroundColor: isCameraActive ? "transparent" : "#f3f4f6",
                            }}></div>
                            {
                                (!loading) &&
                                <p className="w-full absolute transform-center text-center left"
                                    style={{
                                        top: "100%",
                                        height: "20px",
                                        color: "white",
                                        fontSize: "0.75rem",
                                    }}
                                >Align QR inside the box</p>
                            }
                        </div>
                    )}
                </div>
            )}

            <div className="vw-100"
                style={{ margin: "10px", height: "50px" }}
            >
                {
                    apiRes &&
                    <Button className="w-full font-sm fw-500" buttonText="Scan Next Ticket" onClick={handleReset} style={{
                        height: "48px",
                        fontSize: "0.95rem",
                    }} />
                }
                {
                    !cameraStatus &&
                    <p
                        style={{
                            textAlign: "center",
                            fontSize: "16px",
                            color: "#e7000b",
                        }}
                    >Please enable camera access in your browser settings to scan</p>
                }
            </div>
        </div >
    );
};

export default TicketScanner;